import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useRoutesList() {
    const toast = useToast()
    const records = ref(null)
    const allRows = ref([])
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    const primaryRole = Array.isArray(userData.roles) && userData.roles.length > 0
        ? userData.roles[0]
        : null
    const isSuperUser = ref(
        userData.super_user === true
        || (primaryRole && (
            primaryRole.id === 1
            || (typeof primaryRole.title === 'string' && primaryRole.title.toLowerCase().includes('super'))
        )),
    )
    const getLocationId = location => {
        if (location === null || location === undefined) return null
        if (typeof location === 'object' && location.id !== undefined && location.id !== null) return location.id

        return location
    }
    const defaultLocationId = getLocationId(
        userData.station && userData.station.location
            ? userData.station.location
            : userData.location,
    )
    const locationFilter = ref(isSuperUser.value ? null : defaultLocationId)
    const isLoading = ref(false)

    const columns = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'name', label: 'Warehouse', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'location', label: 'Location', sortable: true },
        { key: 'warehouseQuantity', label: 'Warehouse Qty', sortable: true },
        { key: 'warehouseMass', label: 'Warehouse Mass', sortable: true },
        { key: 'transitQuantity', label: 'Transit Qty', sortable: true },
        { key: 'transitMass', label: 'Transit Mass', sortable: true },
        'actions',
    ]

    const from = ref(0)
    const to = ref(0)
    const meta = ref({ from: 0, to: 0, total: 0 })
    const perPage = ref(10)
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPageOptions = [5, 10, 25, 50, 100]
    const searchQuery = ref('')
    const sortBy = ref('id')
    const isSortDirDesc = ref(true)
    const requiresLocationSelection = computed(() => isSuperUser.value && !locationFilter.value)
    const emptyStateText = computed(() => {
        if (requiresLocationSelection.value) return 'Select a location to load warehouse stock.'
        if (isLoading.value) return 'Loading warehouse stock...'

        return 'No records found'
    })

    const normalizeNumber = value => {
        if (value === null || value === undefined || value === '') return 0

        const parsedValue = Number(value)

        return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    const normalizeText = value => (value === null || value === undefined ? '' : `${value}`.trim())

    const getStockPayloadRows = payload => {
        if (Array.isArray(payload)) return payload
        if (payload && Array.isArray(payload.warehouses)) return payload.warehouses
        if (payload && payload.warehouse && typeof payload.warehouse === 'object') return [payload]
        if (payload && Array.isArray(payload.data)) return payload.data
        if (payload && payload.data && Array.isArray(payload.data.warehouses)) return payload.data.warehouses
        if (payload && payload.data && payload.data.warehouse && typeof payload.data.warehouse === 'object') return [payload.data]
        if (payload && Array.isArray(payload.records)) return payload.records
        if (payload && payload.data && Array.isArray(payload.data.records)) return payload.data.records
        if (payload && payload.warehouse_stock && payload.warehouse_stock.warehouse && typeof payload.warehouse_stock.warehouse === 'object') {
            return [payload.warehouse_stock]
        }
        if (
            payload
            && payload.data
            && payload.data.warehouse_stock
            && payload.data.warehouse_stock.warehouse
            && typeof payload.data.warehouse_stock.warehouse === 'object'
        ) {
            return [payload.data.warehouse_stock]
        }
        if (payload && payload.data && Array.isArray(payload.data.warehouse_stock)) return payload.data.warehouse_stock
        if (payload && Array.isArray(payload.warehouse_stock)) return payload.warehouse_stock

        return []
    }

    const normalizeWarehouseRow = stockRow => {
        const warehouse = stockRow && stockRow.warehouse && typeof stockRow.warehouse === 'object'
            ? stockRow.warehouse
            : {}
        const warehouseStock = stockRow && stockRow.stock_in_warehouse && typeof stockRow.stock_in_warehouse === 'object'
            ? stockRow.stock_in_warehouse
            : {}
        const transitStock = stockRow && stockRow.stock_in_transit && typeof stockRow.stock_in_transit === 'object'
            ? stockRow.stock_in_transit
            : {}
        const explicitLocation = stockRow && stockRow.location && typeof stockRow.location === 'object'
            ? stockRow.location
            : null
        const locationName = normalizeText(
            warehouse.location_name
            || stockRow.location_name
            || (explicitLocation && explicitLocation.name ? explicitLocation.name : ''),
        )
        const locationId = warehouse.location_id || stockRow.location_id || (explicitLocation ? explicitLocation.id : null)
        const location = locationName
            ? {
                ...(explicitLocation && typeof explicitLocation === 'object' ? explicitLocation : {}),
                id: locationId,
                name: locationName,
            }
            : explicitLocation

        return {
            ...stockRow,
            id: warehouse.id || null,
            code: normalizeText(warehouse.code),
            name: normalizeText(warehouse.name),
            location,
            locationName: locationName || null,
            warehouseQuantity: normalizeNumber(warehouseStock.quantity),
            warehouseMass: normalizeNumber(warehouseStock.mass),
            transitQuantity: normalizeNumber(transitStock.quantity),
            transitMass: normalizeNumber(transitStock.mass),
            offloadedBales: normalizeNumber(warehouseStock.quantity),
            offloadedBalesMass: normalizeNumber(warehouseStock.mass),
            transitBales: normalizeNumber(transitStock.quantity),
            transitBalesMass: normalizeNumber(transitStock.mass),
            stock: {
                quantity: normalizeNumber(warehouseStock.quantity),
                mass: normalizeNumber(warehouseStock.mass),
                transitBales: normalizeNumber(transitStock.quantity),
                transitMass: normalizeNumber(transitStock.mass),
            },
            bales: Array.isArray(stockRow && stockRow.bales) ? stockRow.bales : [],
        }
    }

    const getLocationLabel = row => {
        if (row && row.location && row.location.name) return normalizeText(row.location.name)
        if (row && row.locationName) return normalizeText(row.locationName)

        return ''
    }

    const matchesSearch = row => {
        const term = normalizeText(searchQuery.value).toLowerCase()

        if (!term) return true

        const haystack = [
            row.id,
            row.code,
            row.name,
            getLocationLabel(row),
            row.warehouseQuantity,
            row.warehouseMass,
            row.transitQuantity,
            row.transitMass,
        ]
            .map(value => normalizeText(value).toLowerCase())
            .join(' ')

        return haystack.includes(term)
    }

    const hasVisibleStock = row => (
        normalizeNumber(row.warehouseQuantity) > 0
        || normalizeNumber(row.warehouseMass) > 0
        || normalizeNumber(row.transitQuantity) > 0
        || normalizeNumber(row.transitMass) > 0
    )

    const getSortableValue = (row, key) => {
        if (key === 'location') return getLocationLabel(row).toLowerCase()
        if (key === 'warehouseQuantity') return normalizeNumber(row.warehouseQuantity)
        if (key === 'warehouseMass') return normalizeNumber(row.warehouseMass)
        if (key === 'transitQuantity') return normalizeNumber(row.transitQuantity)
        if (key === 'transitMass') return normalizeNumber(row.transitMass)

        return row && row[key] !== undefined && row[key] !== null ? row[key] : ''
    }

    const sortRows = rows => {
        const activeSortKey = sortBy.value || 'id'

        return [...rows].sort((leftRow, rightRow) => {
            const leftValue = getSortableValue(leftRow, activeSortKey)
            const rightValue = getSortableValue(rightRow, activeSortKey)

            if (typeof leftValue === 'number' && typeof rightValue === 'number') {
                return isSortDirDesc.value ? rightValue - leftValue : leftValue - rightValue
            }

            const comparison = normalizeText(leftValue).localeCompare(normalizeText(rightValue), undefined, {
                numeric: true,
                sensitivity: 'base',
            })

            return isSortDirDesc.value ? comparison * -1 : comparison
        })
    }

    const updateMeta = total => {
        const startIndex = total === 0 ? 0 : (currentPage.value - 1) * perPage.value + 1
        const endIndex = total === 0 ? 0 : Math.min(currentPage.value * perPage.value, total)

        from.value = startIndex
        to.value = endIndex
        totalRecords.value = total
        meta.value = { from: startIndex, to: endIndex, total }
    }

    const processedRows = computed(() => sortRows(
        allRows.value
            .filter(hasVisibleStock)
            .filter(matchesSearch),
    ))

    const tableRows = computed(() => {
        const pageStart = (currentPage.value - 1) * perPage.value
        const pageEnd = pageStart + perPage.value

        return processedRows.value.slice(pageStart, pageEnd)
    })

    const syncMeta = total => {
        const totalPages = total === 0 ? 1 : Math.ceil(total / perPage.value)

        if (currentPage.value > totalPages) currentPage.value = totalPages

        updateMeta(total)
    }

    const loadRows = async () => {
        if (requiresLocationSelection.value) {
            allRows.value = []
            updateMeta(0)
            return
        }

        isLoading.value = true

        try {
            const response = await store.dispatch('cromis-warehouse/list', {
                location_id: locationFilter.value,
            })
            const payload = response && response.data ? response.data : []
            allRows.value = getStockPayloadRows(payload).map(normalizeWarehouseRow)
        } catch (error) {
            allRows.value = []
            updateMeta(0)
            toast({
                component: ToastificationContent,
                props: {
                    title: 'Error fetching warehouses list',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                },
            })
        } finally {
            isLoading.value = false
        }
    }

    const refetch = () => {
        loadRows()
    }

    watch(locationFilter, () => {
        currentPage.value = 1
        loadRows()
    }, { immediate: true })

    watch([processedRows, currentPage, perPage], () => {
        syncMeta(processedRows.value.length)
    }, { immediate: true })

    watch([searchQuery, sortBy, isSortDirDesc], () => {
        currentPage.value = 1
    })

    const resolveGrowerTypeVariant = society => {
        if (society !== null) return 'success'
        return 'primary'
    }

    const resolveStatusVariant = status => {
        if (status === 1) return 'success'
        if (status === 0) return 'secondary'
        return 'warning'
    }

    return {
        tableRows,
        columns,
        perPage,
        currentPage,
        totalRecords,
        from,
        to,
        meta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        records,
        resolveGrowerTypeVariant,
        resolveStatusVariant,
        refetch,
        locationFilter,
        isLoading,
        isSuperUser,
        requiresLocationSelection,
        emptyStateText,
    }
}
